import React, { useCallback, useEffect, useState } from 'react';
import { apiCall } from '../../../utils/api';
import { useLiveRefresh } from '../../../hooks/useLiveRefresh';
import { validators } from '../../../utils/validation';
import { toast } from '../../ui/sonner';
import SensitiveGate from '../../security/SensitiveGate';
import {
  Archive, ArchiveRestore, Trash2, Pencil, UserCog, RefreshCw,
  AlertTriangle, CheckCircle2, XCircle, Users,
} from 'lucide-react';

const STATUS_BADGE = {
  active: 'bg-emerald-100 text-emerald-700',
  inactive: 'bg-slate-100 text-slate-600',
  archived: 'bg-amber-100 text-amber-700',
  pending_deletion: 'bg-red-100 text-red-700',
  merged: 'bg-purple-100 text-purple-700',
  closed: 'bg-slate-200 text-slate-700',
};
const REASONS = [
  ['duplicate', 'Duplicate Program'], ['closed', 'Closed Program'],
  ['created_in_error', 'Created in Error'], ['merged', 'Merged into Another Program'],
  ['other', 'Other'],
];

export default function ProgramLifecyclePanel() {
  const [stats, setStats] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [requests, setRequests] = useState([]);
  const [managers, setManagers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState([]);
  const [modal, setModal] = useState(null); // {type, program?, request?}
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [reauth, setReauth] = useState(null); // pending approve action after verify

  const load = useCallback(async () => {
    try {
      const [s, p, r, m] = await Promise.all([
        apiCall('/api/employer/group-homes/lifecycle-stats'),
        apiCall('/api/employer/group-homes/programs?status=all'),
        apiCall('/api/employer/group-homes/delete-requests'),
        apiCall('/api/employer/group-homes/pm-directory'),
      ]);
      setStats(s); setPrograms(p.programs || []); setRequests(r.requests || []); setManagers(m.managers || []);
    } catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Live sync: lifecycle board reflects archive/restore/delete approvals instantly.
  useLiveRefresh(['programs', 'group_homes', 'approvals'], load);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); setModal(null); setForm({}); setSelected([]); load(); }
    catch (e) {
      const d = e.message || 'Action failed';
      toast.error(typeof d === 'string' ? d : 'Blocked by dependencies');
    } finally { setBusy(false); }
  };

  const archive = (p) => act(() => apiCall(`/api/employer/group-homes/programs/${p._id}/archive`, { method: 'POST' }), `'${p.name}' archived`);
  const restore = (p) => act(() => apiCall(`/api/employer/group-homes/programs/${p._id}/restore`, { method: 'POST' }), `'${p.name}' restored`);
  const bulkArchive = () => act(() => apiCall('/api/employer/group-homes/programs/archive-bulk', {
    method: 'POST', body: JSON.stringify({ program_ids: selected }) }), `${selected.length} program(s) archived`);

  const submitDeleteRequest = () => {
    const err = validators.required(form.reason) || validators.minLength(10, 'Reason')(form.reason);
    if (err) { toast.error(err); return; }
    act(() => apiCall(`/api/employer/group-homes/programs/${modal.program._id}/delete-request`, {
      method: 'POST', body: JSON.stringify({ reason_category: form.reason_category || 'other', reason: form.reason }),
    }), 'Deletion request submitted — Super Administrator approval required');
  };

  const approve = (req, archiveInstead = false) =>
    act(() => apiCall(`/api/employer/group-homes/delete-requests/${req._id}/approve`, {
      method: 'POST', body: JSON.stringify({ archive_instead: archiveInstead }),
    }), archiveInstead ? 'Program archived instead' : 'Program permanently deleted');

  const startApprove = (req, archiveInstead = false) => setReauth({ req, archiveInstead });

  const reject = () => {
    if (!form.reason || form.reason.trim().length < 5) { toast.error('Please provide a rejection reason.'); return; }
    act(() => apiCall(`/api/employer/group-homes/delete-requests/${modal.request._id}/reject`, {
      method: 'POST', body: JSON.stringify({ reason: form.reason }) }), 'Request rejected — program restored to active');
  };

  const reassign = () => {
    if (!form.new_manager) { toast.error('Select a replacement manager'); return; }
    const ids = modal.type === 'bulk-reassign' ? selected : [modal.program._id];
    act(() => apiCall(ids.length > 1 || modal.type === 'bulk-reassign'
      ? '/api/employer/group-homes/reassign-manager-bulk'
      : `/api/employer/group-homes/programs/${ids[0]}/reassign-manager`, {
      method: 'POST',
      body: JSON.stringify(ids.length > 1 || modal.type === 'bulk-reassign'
        ? { program_ids: ids, new_manager_user_id: form.new_manager }
        : { new_manager_user_id: form.new_manager }),
    }), 'Program Manager reassigned');
  };

  const archivePm = (m, status) => act(() => apiCall(`/api/employer/group-homes/managers/${m.user_id}/archive`, {
    method: 'POST', body: JSON.stringify({ status }) }), `${m.full_name} set to ${status}`);

  const shown = programs.filter((p) => filter === 'all' ? true : (p.status || 'active') === filter);
  const pending = requests.filter((r) => ['pending', 'info_requested'].includes(r.status));
  const assignablePms = managers.filter((m) => !['archived', 'resigned', 'terminated', 'suspended'].includes(m.status));
  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  return (
    <div className="space-y-6" data-testid="program-lifecycle-panel">
      {/* Widgets */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[['Active Programs', stats.active_programs, 'text-emerald-600'],
            ['Archived', stats.archived_programs, 'text-amber-600'],
            ['Pending Deletes', stats.pending_delete_requests, 'text-red-600'],
            ['Without PM', stats.programs_without_pm, 'text-orange-600'],
            ['Inactive PMs', stats.inactive_pms, 'text-slate-600'],
            ['Archived PMs', stats.archived_pms, 'text-slate-500']].map(([label, val, color]) => (
            <div key={label} className="bg-white rounded-xl border border-slate-200 p-3" data-testid={`lifecycle-stat-${label.toLowerCase().replace(/ /g, '-')}`}>
              <p className="text-xs text-slate-500">{label}</p>
              <p className={`text-xl font-bold ${color}`}>{val}</p>
            </div>
          ))}
        </div>
      )}

      {/* Pending approvals */}
      {pending.length > 0 && (
        <div className="bg-white rounded-2xl border border-red-200 p-5" data-testid="pending-delete-requests">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-red-500" /> Pending Deletion Approvals ({pending.length})
          </h3>
          {pending.map((r) => (
            <div key={r._id} className="border border-slate-100 rounded-xl p-4 mb-2">
              <div className="flex flex-wrap justify-between gap-3">
                <div className="text-sm">
                  <p className="font-semibold text-slate-900">{r.program_name}</p>
                  <p className="text-slate-500">Requested by {r.requested_by_name} · {new Date(r.created_at).toLocaleString()} · <span className="capitalize">{(r.reason_category || '').replace(/_/g, ' ')}</span></p>
                  <p className="text-slate-600 mt-1">"{r.reason}"</p>
                  {r.dependencies && (
                    <p className="text-xs mt-1 text-slate-500">
                      Locations: {r.dependencies.locations} · Managers: {r.dependencies.assigned_managers} · Future shifts: <span className={r.dependencies.future_shifts ? 'text-red-600 font-semibold' : ''}>{r.dependencies.future_shifts}</span> · Active workers: <span className={r.dependencies.active_workers ? 'text-red-600 font-semibold' : ''}>{r.dependencies.active_workers}</span> · History: {r.dependencies.total_jobs} jobs
                    </p>
                  )}
                </div>
                <div className="flex gap-2 items-start">
                  {r.dependencies?.blocking ? (
                    <button onClick={() => startApprove(r, true)} className="h-9 px-3 rounded-full bg-amber-500 text-white text-xs font-semibold" data-testid={`approve-archive-instead-${r._id}`}>
                      <Archive className="w-3.5 h-3.5 inline mr-1" />Archive Instead
                    </button>
                  ) : (
                    <button onClick={() => startApprove(r, false)} className="h-9 px-3 rounded-full bg-red-600 text-white text-xs font-semibold" data-testid={`approve-delete-${r._id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />Approve & Delete
                    </button>
                  )}
                  <button onClick={() => { setModal({ type: 'reject', request: r }); setForm({}); }} className="h-9 px-3 rounded-full border border-slate-300 text-slate-600 text-xs font-semibold" data-testid={`reject-delete-${r._id}`}>
                    <XCircle className="w-3.5 h-3.5 inline mr-1" />Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Program table */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
          <h3 className="font-semibold text-slate-900">Programs / Group Homes</h3>
          <div className="flex gap-2 items-center">
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-9 px-2 border border-slate-200 rounded-lg text-sm" data-testid="lifecycle-status-filter">
              <option value="all">All statuses</option>
              {Object.keys(STATUS_BADGE).map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>
            {selected.length > 0 && (
              <>
                <button onClick={bulkArchive} className="h-9 px-3 rounded-full bg-amber-500 text-white text-xs font-semibold" data-testid="bulk-archive-btn">Archive {selected.length}</button>
                <button onClick={() => { setModal({ type: 'bulk-reassign' }); setForm({}); }} className="h-9 px-3 rounded-full bg-indigo-500 text-white text-xs font-semibold" data-testid="bulk-reassign-btn">Reassign PM ({selected.length})</button>
              </>
            )}
            <button onClick={load} className="h-9 w-9 rounded-full text-slate-500 hover:bg-slate-100 flex items-center justify-center"><RefreshCw className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-slate-500 uppercase">
              <tr><th className="py-2 pr-2 w-8"></th><th className="py-2 pr-4">Program</th><th className="py-2 pr-4">Status</th><th className="py-2 pr-4">Locations</th><th className="py-2">Actions</th></tr>
            </thead>
            <tbody data-testid="lifecycle-program-table">
              {shown.map((p) => (
                <tr key={p._id} className="border-t border-slate-100">
                  <td className="py-2 pr-2"><input type="checkbox" checked={selected.includes(p._id)} onChange={() => toggle(p._id)} data-testid={`select-program-${p._id}`} /></td>
                  <td className="py-2 pr-4 font-medium text-slate-900">{p.name}</td>
                  <td className="py-2 pr-4"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[p.status || 'active'] || STATUS_BADGE.inactive}`}>{(p.status || 'active').replace(/_/g, ' ')}</span></td>
                  <td className="py-2 pr-4 text-slate-600">{p.location_count ?? 0}</td>
                  <td className="py-2">
                    <div className="flex gap-1">
                      <button title="Edit" onClick={() => { setModal({ type: 'edit', program: p }); setForm({ name: p.name, description: p.description || '', contact_phone: p.contact_phone || '', contact_email: p.contact_email || '' }); }} className="h-8 w-8 rounded-full text-slate-500 hover:bg-slate-100 flex items-center justify-center" data-testid={`edit-program-${p._id}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button title="Reassign PM" onClick={() => { setModal({ type: 'reassign', program: p }); setForm({}); }} className="h-8 w-8 rounded-full text-indigo-500 hover:bg-indigo-50 flex items-center justify-center" data-testid={`reassign-pm-${p._id}`}><UserCog className="w-3.5 h-3.5" /></button>
                      {(p.status || 'active') === 'archived' ? (
                        <button title="Restore" onClick={() => restore(p)} className="h-8 w-8 rounded-full text-emerald-500 hover:bg-emerald-50 flex items-center justify-center" data-testid={`restore-program-${p._id}`}><ArchiveRestore className="w-3.5 h-3.5" /></button>
                      ) : (
                        <button title="Archive" onClick={() => archive(p)} className="h-8 w-8 rounded-full text-amber-500 hover:bg-amber-50 flex items-center justify-center" data-testid={`archive-program-${p._id}`}><Archive className="w-3.5 h-3.5" /></button>
                      )}
                      {(p.status || 'active') !== 'pending_deletion' && (
                        <button title="Request deletion" onClick={() => { setModal({ type: 'delete-request', program: p }); setForm({ reason_category: 'other', reason: '' }); }} className="h-8 w-8 rounded-full text-red-500 hover:bg-red-50 flex items-center justify-center" data-testid={`delete-request-${p._id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {shown.length === 0 && <tr><td colSpan="5" className="py-4 text-slate-400">No programs match this filter.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* PM directory */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="pm-directory">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-3"><Users className="w-5 h-5 text-indigo-500" /> Program Managers</h3>
        {managers.map((m) => (
          <div key={m.user_id} className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 py-2 text-sm">
            <div>
              <span className="font-medium text-slate-900">{m.full_name}</span>
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${m.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{m.status}</span>
              <p className="text-xs text-slate-400">{m.email} · {m.active_assignments.map((a) => a.name).filter(Boolean).join(', ') || 'No active programs'}</p>
            </div>
            <select value="" onChange={(e) => e.target.value && archivePm(m, e.target.value)} className="h-8 px-2 border border-slate-200 rounded-lg text-xs" data-testid={`pm-status-select-${m.user_id}`}>
              <option value="">Set status…</option>
              {['active', 'inactive', 'archived', 'suspended', 'resigned', 'terminated'].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        ))}
        {managers.length === 0 && <p className="text-sm text-slate-400">No program managers yet.</p>}
      </div>

      {/* Step-up re-auth for approvals */}
      {reauth && (
        <SensitiveGate title="deletion approval" onCancel={() => setReauth(null)}
          onVerified={() => { const { req, archiveInstead } = reauth; setReauth(null); approve(req, archiveInstead); }} />
      )}

      {/* Modals */}
      {modal && (
        <div className="fixed inset-0 bg-black/50 z-[90] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            {modal.type === 'delete-request' && (
              <>
                <h3 className="font-bold text-slate-900">Delete "{modal.program.name}"</h3>
                <p className="text-sm text-amber-600 mt-1">This action requires Super Administrator approval.</p>
                <label className="block text-sm font-medium text-slate-700 mt-4 mb-1">Reason for deletion</label>
                <select value={form.reason_category} onChange={(e) => setForm({ ...form, reason_category: e.target.value })} className="w-full h-10 px-2 border border-slate-200 rounded-lg text-sm mb-3" data-testid="delete-reason-category">
                  {REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Explain why this program should be deleted (min 10 characters)" rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="delete-reason-text" />
                <div className="flex gap-2 mt-4">
                  <button disabled={busy} onClick={submitDeleteRequest} className="flex-1 h-10 rounded-full bg-red-600 text-white text-sm font-semibold disabled:opacity-50" data-testid="submit-delete-request-btn">Submit Request</button>
                  <button onClick={() => setModal(null)} className="flex-1 h-10 rounded-full border border-slate-200 text-sm">Cancel</button>
                </div>
              </>
            )}
            {modal.type === 'reject' && (
              <>
                <h3 className="font-bold text-slate-900">Reject deletion of "{modal.request.program_name}"</h3>
                <textarea value={form.reason || ''} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Rejection reason" rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mt-3" data-testid="reject-reason-text" />
                <div className="flex gap-2 mt-4">
                  <button disabled={busy} onClick={reject} className="flex-1 h-10 rounded-full bg-slate-800 text-white text-sm font-semibold disabled:opacity-50" data-testid="confirm-reject-btn">Reject Request</button>
                  <button onClick={() => setModal(null)} className="flex-1 h-10 rounded-full border border-slate-200 text-sm">Cancel</button>
                </div>
              </>
            )}
            {(modal.type === 'reassign' || modal.type === 'bulk-reassign') && (
              <>
                <h3 className="font-bold text-slate-900">
                  {modal.type === 'bulk-reassign' ? `Reassign PM for ${selected.length} programs` : `Change Program Manager — ${modal.program.name}`}
                </h3>
                <p className="text-xs text-slate-500 mt-1">Open tasks, schedules, notifications and permissions transfer to the new manager. Both managers are notified.</p>
                <select value={form.new_manager || ''} onChange={(e) => setForm({ ...form, new_manager: e.target.value })} className="w-full h-10 px-2 border border-slate-200 rounded-lg text-sm mt-3" data-testid="reassign-pm-select">
                  <option value="">Select replacement manager…</option>
                  {assignablePms.map((m) => <option key={m.user_id} value={m.user_id}>{m.full_name} ({m.email})</option>)}
                </select>
                <div className="flex gap-2 mt-4">
                  <button disabled={busy} onClick={reassign} className="flex-1 h-10 rounded-full bg-indigo-600 text-white text-sm font-semibold disabled:opacity-50" data-testid="confirm-reassign-btn">Transfer Ownership</button>
                  <button onClick={() => setModal(null)} className="flex-1 h-10 rounded-full border border-slate-200 text-sm">Cancel</button>
                </div>
              </>
            )}
            {modal.type === 'edit' && (
              <>
                <h3 className="font-bold text-slate-900">Edit "{modal.program.name}"</h3>
                <div className="space-y-2 mt-3">
                  <input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Program name" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm" data-testid="edit-program-name" />
                  <input value={form.contact_email || ''} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="Contact email" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm" data-testid="edit-program-email" />
                  <input value={form.contact_phone || ''} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="Contact phone" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm" data-testid="edit-program-phone" />
                  <textarea value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description / notes" rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="edit-program-description" />
                </div>
                <div className="flex gap-2 mt-4">
                  <button disabled={busy} onClick={() => {
                    const err = validators.required(form.name) || validators.companyName(form.name) || validators.email(form.contact_email) || validators.phone(form.contact_phone);
                    if (err) { toast.error(err); return; }
                    act(() => apiCall(`/api/employer/group-homes/programs/${modal.program._id}`, { method: 'PUT', body: JSON.stringify(form) }), 'Program updated');
                  }} className="flex-1 h-10 rounded-full bg-cyan-500 text-white text-sm font-semibold disabled:opacity-50" data-testid="save-edit-program-btn">Save Changes</button>
                  <button onClick={() => setModal(null)} className="flex-1 h-10 rounded-full border border-slate-200 text-sm">Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
